import { dial10, fmtScore } from '@shared/scoring';

export interface RevealRow {
  name: string;
  /** 0–100. */
  value: number;
  points: number;
  total: number;
  isYou?: boolean;
}

/**
 * One sentence, naming people, above the score list.
 *
 * A list is scanned; a sentence is read aloud on a call, and the person named
 * as furthest away defends themselves — which is the argument, which is the
 * game. It is also the only thing that makes a clue pitched at one confederate
 * visible: "Sam earned 0.8 — Jo was 0.2 away, Kim was 21.0 away" is
 * self-policing among friends in a way a scoreboard never is.
 */
export function revealSentence(giverName: string, giverPoints: number, rows: RevealRow[], target: number): string {
  if (rows.length === 0) return `${giverName} earned nothing — nobody guessed.`;
  const byDistance = rows
    .slice()
    .sort((a, b) => Math.abs(a.value - target) - Math.abs(b.value - target));

  // The verb is stated once and then elided, and the last clause gets an "and".
  // Four identical "X was N away" clauses is a drone on a call; this is a
  // sentence someone can actually read out.
  const clauses = byDistance.map((r, i) => {
    const away = dial10(Math.abs(r.value - target));
    return i === 0 ? `${r.name} was ${away} away` : `${r.name} ${away}`;
  });
  const list =
    clauses.length > 1
      ? `${clauses.slice(0, -1).join(', ')}, and ${clauses[clauses.length - 1]}`
      : clauses[0];

  // The confederate detector, said out loud: one bullseye and a field that
  // missed entirely is what a clue pitched at one person looks like, and it is
  // invisible on a scoreboard.
  const points = byDistance.map((r) => r.points);
  const lonelyBullseye = points.length > 2 && points[0] === 4 && points.slice(1).every((p) => p === 0);
  const tail = lonelyBullseye ? `. Only ${byDistance[0].name} was close.` : '.';

  return `${giverName} earned ${fmtScore(giverPoints)} — ${list}${tail}`;
}

export function ScoreList({
  giverName,
  giverClue,
  giverPoints,
  giverTotal,
  rows,
}: {
  giverName: string;
  giverClue: string;
  giverPoints: number;
  giverTotal: number;
  rows: RevealRow[];
}) {
  const sorted = rows.slice().sort((a, b) => b.points - a.points);
  return (
    <ul className="scores">
      <li className={giverPoints === 0 ? 'zero' : undefined}>
        <span className="tag">Clue</span>
        <span className="who">
          {giverName} · “{giverClue}”
        </span>
        <span className="pts">
          {giverPoints > 0 ? '+' : ''}
          {fmtScore(giverPoints)}
        </span>
        <span className="tot">{fmtScore(giverTotal)}</span>
      </li>
      {sorted.map((r) => (
        <li key={r.name} className={r.points === 0 ? 'zero' : undefined}>
          <span className="tag">{dial10(r.value)}</span>
          <span className="who">
            {r.name}
            {r.isYou ? ' (you)' : ''}
          </span>
          <span className="pts">
            {r.points > 0 ? '+' : ''}
            {r.points}
          </span>
          <span className="tot">{fmtScore(r.total)}</span>
        </li>
      ))}
    </ul>
  );
}
